import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    IInteriorBranding,
    IInteriorBrandingPayload,
    brandingDraftAtom,
    brandingLoadingAtom,
    brandingSavingAtom,
    interiorBrandingAtom
} from "./types";

type FetchBranding = () => Promise<IInteriorBranding | null>;
type SaveBranding = (payload: IInteriorBrandingPayload) => Promise<IInteriorBranding | null>;
type UploadImage = (file: File) => Promise<IInteriorBranding | null>;

export type InteriorBrandingFunctions = {
    fetchBranding: FetchBranding;
    saveBranding: SaveBranding;
    uploadLogo: UploadImage;
    uploadCover: UploadImage;
};

type UseInteriorBranding = [IInteriorBranding | null, InteriorBrandingFunctions];

// #region useInteriorBranding
// Brand-scoped branding profile for the interior-designer share page. The brand
// is resolved server-side from the auth token — never sent from the client.
export const useInteriorBranding = (): UseInteriorBranding => {
    const [branding, setBranding] = useAtom(interiorBrandingAtom);
    const setDraft = useSetAtom(brandingDraftAtom);
    const setLoading = useSetAtom(brandingLoadingAtom);
    const setSaving = useSetAtom(brandingSavingAtom);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    // #region fetchBranding
    const fetchBranding: FetchBranding = async (): Promise<IInteriorBranding | null> => {
        if (!authToken) return null;
        setLoading(true);

        const response: AxiosResponse<IInteriorBranding> | void = await Axios.get(
            `${environment.interiorBrandingUrl}`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        setLoading(false);
        if (response?.data) {
            setBranding(response.data);
            setDraft(response.data);
            return response.data;
        }
        return null;
    };

    // #region saveBranding
    const saveBranding: SaveBranding = async (payload: IInteriorBrandingPayload): Promise<IInteriorBranding | null> => {
        if (!authToken) return null;
        setSaving(true);

        const response: AxiosResponse<IInteriorBranding> | void = await Axios.put(
            `${environment.interiorBrandingUrl}`,
            payload,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        setSaving(false);
        if (response?.data) {
            setBranding(response.data);
            setDraft(response.data);
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Branding saved.' });
            return response.data;
        }

        setSnackbar({ show: true, snackbarLevel: 'error', text: 'There was an error while saving your branding.' });
        return null;
    };

    // #region uploadImage
    // Upload-then-persist: the endpoint writes to S3 and stores the resulting
    // URL, returning the full resolved branding so the preview updates at once.
    const uploadImage = async (path: 'logo' | 'cover', field: string, file: File): Promise<IInteriorBranding | null> => {
        if (!authToken) return null;
        setSaving(true);

        const form = new FormData();
        form.append(field, file);

        const response: AxiosResponse<IInteriorBranding> | void = await Axios.put(
            `${environment.interiorBrandingUrl}/${path}`,
            form,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-Wallmates-Auth': authToken
                }
            }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        setSaving(false);
        if (response?.data) {
            setBranding(response.data);
            // Only the image URL is authoritative from this call — keep the
            // designer's other unsaved edits in the draft.
            setDraft((current: IInteriorBranding | null) => (current
                ? { ...current, logo_url: response.data.logo_url, cover_image_url: response.data.cover_image_url }
                : response.data));
            return response.data;
        }

        setSnackbar({ show: true, snackbarLevel: 'error', text: `There was an error while uploading the ${path} image.` });
        return null;
    };

    const uploadLogo: UploadImage = (file: File) => uploadImage('logo', 'branding_logo', file);
    const uploadCover: UploadImage = (file: File) => uploadImage('cover', 'branding_cover', file);

    return [branding, { fetchBranding, saveBranding, uploadLogo, uploadCover }];
};
